import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { cloneDeep } from 'lodash-es'

import { toast } from '../utils/helpers'

import { convertIncomingData, convertOutgoingData } from './transforms'

/**
 * The root store, handed over by RootStore once it exists.
 *
 * This used to be `import { store } from '../stores/RootStore'`, which closed a cycle:
 * UserStore -> user.service -> instance -> RootStore -> UserStore. RootStore builds its
 * `store` at module scope, so whenever the graph happened to enter that loop from the
 * wrong side, `RootStore`'s `user = user` field read a binding still in its temporal
 * dead zone and the whole app failed to boot with "Cannot access 'user' before
 * initialization". Which side it entered from depended on unrelated imports elsewhere,
 * so the breakage moved around whenever an import was added or removed.
 *
 * Every use of the store here is inside an interceptor, so nothing needs it at module
 * scope. Inverting it removes the edge entirely.
 */
let store = null

export const registerStore = (rootStore) => {
  store = rootStore
}

// Default config options
const DEFAULT_OPTIONS = {
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30 * 1000, // in ms
  withCredentials: true, // Required for SSO
}

// Create instance
const instance = axios.create(DEFAULT_OPTIONS)

// Initialize authentication interceptor to automatically attempt to refresh
// an expired token
const triggerRefresh = (failedRequest) => {
  if (store) {
    return store.triggerRefreshToken(failedRequest)
  }

  // eslint-disable-next-line no-console
  console.debug('`store` is not defined, cannot refresh expired token.')
  return null
}
createAuthRefreshInterceptor(instance, triggerRefresh, {
  statusCodes: [401], // Updated BE triggers 401 error rather than a 403
  // If the request that returned a 401 error does include a `code` of `token_not_valid`, this is not
  // an access token issue and should be ignored
  shouldRefresh: (error) =>
    error.response.data.code === 'token_not_valid' && error.response.data.messages,
})

/**
 * WARP-567 — the client-side mirror of the server's read-only impersonation rule.
 *
 * `SessionEnforcedJWTAuthentication` already refuses every one of these, so this is not
 * the security boundary. It exists because a number of writes fire on their own rather
 * than from a button a user pressed — marking notifications and announcements read, for
 * instance — and letting those reach the server would spend a request per page load
 * only to collect a 403, and surface errors for something the user never asked for.
 * Failing them here keeps an impersonated session quiet and honest.
 *
 * The exceptions are the calls an impersonated session needs to stay alive or to end
 * itself; without them the read-only block would trap the user in the session.
 */
const WRITE_METHODS = ['post', 'put', 'patch', 'delete']
const PATHS_ALLOWED_WHILE_IMPERSONATING = [
  '/impersonate/stop/',
  '/logout/',
  '/token/refresh/',
  '/token/verify/',
]
const READ_ONLY_MESSAGE = 'You are viewing as another user. Exit before making any changes.'

const isBlockedWhileImpersonating = (config) => {
  if (!store?.user?.isImpersonating) return false
  if (!WRITE_METHODS.includes((config.method || 'get').toLowerCase())) return false

  const url = config.url || ''
  return !PATHS_ALLOWED_WHILE_IMPERSONATING.some((path) => url.startsWith(path))
}

instance.interceptors.request.use((config) => {
  if (isBlockedWhileImpersonating(config)) {
    const error = new Error(READ_ONLY_MESSAGE)
    // Shaped like a server rejection so `getErrorMessage` reads the same message off it
    // as it would from the backend's own 403.
    error.response = { status: 403, data: { detail: READ_ONLY_MESSAGE } }
    error.impersonationBlocked = true
    return Promise.reject(error)
  }

  const _config = cloneDeep(config)

  // `cloneDeep` mangles a passed AbortSignal (it's an EventTarget, not a plain
  // object), which silently breaks request cancellation. Restore the live
  // signal reference so `controller.abort()` actually cancels the request.
  if (config.signal) {
    _config.signal = config.signal
  }

  // Set the AUTH token for any request
  // If we have a user token, update the config
  if (store.user.accessToken && store.user.accessToken !== '') {
    _config.headers.Authorization = `Bearer ${store.user.accessToken}`
  }

  // Bypass the conversion if the data is FormData
  if (config.data instanceof FormData) {
    delete _config.headers['Content-Type']
    // If it's FormData, just return the config as it is
    return _config
  }

  // Check if `config.data` is a string, if so we need to parse it to an object in order to properly convert the data
  if (typeof config.data === 'string') {
    _config.data = convertOutgoingData(JSON.parse(config.data))
  }
  // Otherwise, convert the data normally
  else {
    _config.data = convertOutgoingData(config.data)
  }

  return _config
})

/**
 * - If a response is successful, only return the data portion of the response
 */
instance.interceptors.response.use(
  (response) => {
    /**
     * NOTE: The response object format is not consistent between the initial request and retried
     * requests!
     *
     * Somewhere along the lines, there's a bug in either our code, axios, or axios-auth-refresh
     * that causes this interceptor to be called with the response object when the initial request is
     * made, but only the response *data* after an auth refresh is triggered. This means that on
     * retried calls, there is no status code, config, etc. available, only the data.
     *
     * In order to avoid assumptions about the existence of the `data` key, the simple workaround
     * here is to first attempt to use `response.data` and fallback to `response` if needed.
     */
    if (response.status === 204 || response.status === 205) {
      return null
    }

    // A file (`responseType: 'blob'`) has no keys to camelCase: the transform would
    // return an empty object in its place.
    if (response.data instanceof Blob) return response.data

    return convertIncomingData(response.data || response, true)
  },
  (error) => {
    // Handles catching errors when the authenticated user is marked as inactive
    // or when their session has been evicted (e.g., exceeded 2-session limit)
    // Clear the store to log the user out and trigger redirect to login
    if (error.response?.status === 401 && store) {
      const message =
        error.response?.data?.detail || 'Your session has expired. Please log in again.'
      toast(message, 'error')
      store.clearStore()
    } else if (error.response?.data?.code === 'user_inactive' && store) {
      toast('Your account has been deactivated.', 'error')
      store.clearStore()
    }
    // Convert response data to camelCase for consumers but preserve the original
    // Error so non-enumerable fields (message, stack) survive for Sentry/logging.
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data = convertIncomingData(error.response.data) // eslint-disable-line no-param-reassign
    }
    throw error
  },
)

export default instance
